/**
 * AnalysisTab — Analysis stage with Accountant Lens sub-navigation
 *
 * Phase 4.5 — redesign-p4-analacct-45b
 * 4 sub-panels: Engine Review & Attest, Methodology Comparison,
 * Analysis Tools / Stress-Test, Restatement Log
 * Hash-based deep-links: #engine-review, #methodology, #stress-test, #restatement-log
 *
 * This component serves the /reports/:reportId/analysis route for the
 * accountant lens of the case workspace.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Shield,
  Scale,
  Activity,
  History,
  FileCheck,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';
import { useAccountantAuth } from '@/contexts/AccountantAuthContext';

// Import sub-panel components
import {
  EngineReviewPanel,
  MethodologyComparisonPanel,
  StressTestPanel,
  RestatementLogPanel,
} from '@/components/analysis';

// Import shared components
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';

// Import API
import { api } from '@/utils/api';

type AnalysisSubTab =
  | 'engine-review'
  | 'methodology'
  | 'stress-test'
  | 'restatement-log';

interface SubTabConfig {
  id: AnalysisSubTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType<{ reportId: string; reportData: any }>;
}

const SUB_TABS: SubTabConfig[] = [
  {
    id: 'engine-review',
    label: 'Engine Review & Attest',
    icon: Shield,
    component: EngineReviewPanel,
  },
  {
    id: 'methodology',
    label: 'Methodology Comparison',
    icon: Scale,
    component: MethodologyComparisonPanel,
  },
  {
    id: 'stress-test',
    label: 'Analysis Tools / Stress-Test',
    icon: Activity,
    component: StressTestPanel,
  },
  {
    id: 'restatement-log',
    label: 'Restatement Log',
    icon: History,
    component: RestatementLogPanel,
  },
];

// Report data interface
interface ReportData {
  id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  business_type: string;
  zone: string;
  event_type?: string;
  event_start_date?: string;
  event_end_date?: string;
  coverage_year?: number;
  status: string;
  created_at: string;
  net_supported_claim?: number | null;
  figures_run_id?: string | null;
  identity_source?: string | null;
  legacy_final_claim_amount?: number | null;
  result?: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    step1_variable_profit_loss?: number;
    step2_incremental_loss?: number;
    causation_test_used?: string;
    calculation_detail?: any;
    payroll_reconciliation?: any;
  };
  financial_data?: any;
}

export function AnalysisTab() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeader } = useAccountantAuth();

  // Get initial tab from hash or default to engine-review
  const getInitialTab = (): AnalysisSubTab => {
    const hash = location.hash.replace('#', '');
    if (hash && SUB_TABS.some((t) => t.id === hash)) {
      return hash as AnalysisSubTab;
    }
    // Check if there's a sub-route
    const subRoute = location.pathname.split('/').pop();
    if (subRoute && SUB_TABS.some((t) => t.id === subRoute)) {
      return subRoute as AnalysisSubTab;
    }
    return 'engine-review';
  };

  const [activeTab, setActiveTab] = useState<AnalysisSubTab>(getInitialTab());
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync hash with active tab
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && SUB_TABS.some((t) => t.id === hash) && hash !== activeTab) {
      setActiveTab(hash as AnalysisSubTab);
    }
  }, [location.hash]);

  // Update hash when tab changes
  const handleTabChange = (tabId: AnalysisSubTab) => {
    setActiveTab(tabId);
    // Update URL hash without navigation
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  // Fetch report data
  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      try {
        setLoading(true);
        // Use the existing collaboration endpoint for external accountants
        // or the engine-review endpoint for internal
        const response = await api.get(`/api/v1/collaboration/reports/${reportId}/view`);
        setReportData(response);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch report:', err);
        setError(err.response?.data?.detail || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  // Get the active tab component
  const ActiveComponent = SUB_TABS.find((t) => t.id === activeTab)?.component || EngineReviewPanel;

  if (!reportId) {
    return (
      <div className="flex h-64 items-center justify-center bg-[var(--color-bg-base)]">
        <div className="text-center">
          <p className="text-[var(--color-text-secondary)]">No case selected.</p>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">Open a case to view analysis.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)]">
        {/* Header */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--color-brand)]" />
              <h1
                className="text-2xl font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Analysis
              </h1>
            </div>
          </div>
        </div>
        {/* Sub-rail placeholder */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex gap-2 py-3">
              {SUB_TABS.map((tab) => (
                <div
                  key={tab.id}
                  className="h-9 w-32 animate-pulse rounded-full bg-[var(--color-bg-elevated)]"
                />
              ))}
            </div>
          </div>
        </div>
        {/* Content placeholder */}
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="h-96 animate-pulse rounded-lg bg-[var(--color-bg-surface)]" />
        </div>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(255,102,0,0.12)]">
              <FileCheck className="h-6 w-6 text-[var(--color-brand)]" />
            </div>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Failed to Load Report
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">{error || 'Report not found'}</p>
            <button
              onClick={() => navigate('/accountant')}
              className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-[var(--color-brand)] hover:bg-[rgba(255,102,0,0.12)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const result = reportData.result;

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      {/* Header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Link
                  to="/accountant"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Link>
                <h1
                  className="text-2xl font-semibold text-[var(--color-text-primary)]"
                  style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
                >
                  Analysis
                </h1>
              </div>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                {reportData.client_name} •{' '}
                <span className="font-mono text-[var(--color-text-dim)]">{reportId}</span>
              </p>
            </div>

            {/* Key Metrics Summary */}
            <div className="flex flex-wrap items-center gap-3">
              {(reportData.net_supported_claim ?? result?.final_claim_amount) != null && (
                <MetricCard
                  label={reportData.net_supported_claim != null ? 'Net supported claim' : 'Final Claim'}
                  value={<FinancialValue value={(reportData.net_supported_claim ?? result?.final_claim_amount) as number} size="lg" />}
                  badge={
                    reportData.status === 'completed'
                      ? { text: 'Complete', variant: 'done' }
                      : { text: 'In Progress', variant: 'active' }
                  }
                  className="min-w-[160px]"
                />
              )}
              {result?.causation_passed !== undefined && (
                <div className="flex items-center gap-2">
                  <StatusBadge
                    text={result.causation_passed ? 'Causation Passed' : 'Causation Failed'}
                    variant={result.causation_passed ? 'done' : 'attention'}
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sub-rail Navigation */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-2 py-3">
            {SUB_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-[rgba(255,102,0,0.12)] text-[var(--color-brand)] ring-1 ring-[rgba(255,102,0,0.30)]'
                      : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)]'
                  )}
                  data-testid={`analysis-subtab-${tab.id}`}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4',
                      isActive ? 'text-[var(--color-brand)]' : 'text-[var(--color-text-dim)]'
                    )}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {activeTab === 'engine-review' ? (
          <EngineReviewPanel
            reportId={reportId}
            reportData={reportData}
            canonicalNet={reportData.net_supported_claim}
          />
        ) : (
          <ActiveComponent reportId={reportId} reportData={reportData} />
        )}
      </div>
    </div>
  );
}

export default AnalysisTab;
